/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav P�ibyl (pribyl@cadwork.cz)
 *
 */

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import Factory from "App/render/webgl/passes/factory";
import WebGLContext from "App/web_gl_context";

import Depth from "App/render/webgl/shaders/depth/depth";
import AlphaDepth from "App/render/webgl/shaders/depth/alpha_depth";
import LineDepth from "App/render/webgl/shaders/depth/line_depth";
import PointDepth from "App/render/webgl/shaders/depth/point_depth";
import {
    AlphaTransparencyBinTypeName,
    BillboardBinTypeName,
    LinesBinTypeName,
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName,
    OutlinesBinTypeName,
    PoiBinTypeName,
    PointsBinTypeName,
    TextBinTypeName,
    TransparentBinTypeName, TransparentNoTextureBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";
import OpaqueShader from "App/render/webgl/shaders/opaque";
import OpaqueNoTextureShader from "App/render/webgl/shaders/opaque_no_texture";

export default 
class OpaqueDepth extends Pass {

    public static TypeName: string = "OpaqueDepth";

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): OpaqueDepth {
        return new OpaqueDepth(aSceneAPI, aRenderGraph);
    }

    public initialize() {
        let shaders = this._renderGraph.getShaders();

        this._shadersToBins = {};

        this.attachShaderToBin(OpaqueBinTypeName, shaders.get(Depth.TypeName));
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(Depth.TypeName));
        this.attachShaderToBin(AlphaTransparencyBinTypeName, shaders.get(AlphaDepth.TypeName));
        this.attachShaderToBin(BillboardBinTypeName, shaders.get(AlphaDepth.TypeName));
        this.attachShaderToBin(TextBinTypeName, shaders.get(Depth.TypeName));
        this.attachShaderToBin(OutlinesBinTypeName, shaders.get(Depth.TypeName));
        this.attachShaderToBin(LinesBinTypeName, shaders.get(LineDepth.TypeName));
        this.attachShaderToBin(PoiBinTypeName, shaders.get(PointDepth.TypeName));
        this.attachShaderToBin(PointsBinTypeName, shaders.get(PointDepth.TypeName));

        this.attachShaderToBin(TransparentBinTypeName, shaders.get(OpaqueShader.TypeName));
        this.attachShaderToBin(TransparentNoTextureBinTypeName, shaders.get(OpaqueNoTextureShader.TypeName));
    }

    public render(aView, aOutputBuffer) {
        let scale = aOutputBuffer.parameters.scale;
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        // set rendering target
        aOutputBuffer.bind();

        // set clear color to one to simulate the depth buffer reset to 1.0 (we render to texture)
        gl.clearColor(1.0, 1.0, 1.0, 1.0);

        // set viewport
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(aView.position[0] * scale, aView.position[1] * scale, aView.size[0] * scale, aView.size[1] * scale);
        gl.scissor(aView.position[0] * scale, aView.position[1] * scale, aView.size[0] * scale, aView.size[1] * scale);

        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // render all the bins related to the opaque depth pass
        let bins = this._renderGraph.getRenderBins();

        this.view = aView;

        let allOpaque =
            !this._sceneAPI.renderSettingsModule.getElementTransparencyEnabled() ||
            (this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverride() &&
                (this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverrideValue() === 0.0));

        if (allOpaque) {
            bins.get(TransparentBinTypeName).render(this);
            bins.get(TransparentNoTextureBinTypeName).render(this);
        }

        let allTransparent =
            this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverride() &&
            (this._sceneAPI.renderSettingsModule.getForceGlobalTransparencyOverrideValue() > 0.0);

        if (!allTransparent) {
            bins.get(OpaqueBinTypeName).render(this);
            bins.get(OpaqueNoTextureBinTypeName).render(this);
        }
        bins.get(AlphaTransparencyBinTypeName).render(this);
        bins.get(BillboardBinTypeName).render(this);
        bins.get(TextBinTypeName).render(this);
        bins.get(OutlinesBinTypeName).render(this);
        bins.get(LinesBinTypeName).render(this);
        bins.get(PoiBinTypeName).render(this);
        bins.get(PointsBinTypeName).render(this);

        gl.disable(gl.SCISSOR_TEST);

        // reset clear color
        gl.clearColor(0.0, 0.0, 0.0, 0.0);

        aOutputBuffer.unbind(this._typeName);

        this.view = null;
    }
}

Factory.register(OpaqueDepth);
